/**
 * Typed wrappers around the backend's teacher endpoints — classroom
 * CRUD, roster management, assignments, gallery review, live class
 * view, and rubric feedback.
 *
 * Pure functions that return parsed bodies. TanStack Query handles
 * caching + invalidation in the per-route hooks. The cookie rides
 * every request via `credentials: 'include'`.
 */
import { apiBaseUrl, AuthApiError } from '../account/authApi';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${apiBaseUrl()}${path}`;
  const headers = new Headers(init.headers ?? {});
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const response = await fetch(url, {
    ...init,
    headers,
    credentials: 'include',
  });
  if (response.status === 204) {
    return undefined as T;
  }
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Empty / non-JSON bodies — fall through.
  }
  if (!response.ok) {
    const detail =
      body && typeof body === 'object' && 'detail' in body && typeof body.detail === 'string'
        ? body.detail
        : null;
    throw new AuthApiError(
      response.status,
      detail,
      `Request to ${path} failed (${response.status}).`,
    );
  }
  return body as T;
}

// --- Classroom shell ---------------------------------------------------

export interface Classroom {
  id: string;
  name: string;
  class_code: string;
  teacher_user_id: string;
  /** Phase 4.5c — when true, kids on the roster can publish to
   *  bryta.app. The teacher owns this flag (one toggle per room). */
  allow_publish?: boolean;
  created_at: string;
  updated_at: string;
}

export async function listClassrooms(): Promise<Classroom[]> {
  return request<Classroom[]>('/classrooms');
}

export async function createClassroom(name: string): Promise<Classroom> {
  return request<Classroom>('/classrooms', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export async function getClassroom(id: string): Promise<Classroom> {
  return request<Classroom>(`/classrooms/${encodeURIComponent(id)}`);
}

export async function patchClassroom(
  id: string,
  args: { name?: string; allow_publish?: boolean },
): Promise<Classroom> {
  return request<Classroom>(`/classrooms/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(args),
  });
}

export async function rotateClassCode(id: string): Promise<Classroom> {
  return request<Classroom>(`/classrooms/${encodeURIComponent(id)}/rotate-code`, {
    method: 'POST',
  });
}

export async function deleteClassroom(id: string): Promise<void> {
  return request<void>(`/classrooms/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/** Kid-readable classroom snapshot — id + name only, no
 *  teacher_user_id / class_code. The student studio's Home banner
 *  uses this. */
export interface ClassroomKidSummary {
  id: string;
  name: string;
}

export async function getClassroomSummary(id: string): Promise<ClassroomKidSummary> {
  return request<ClassroomKidSummary>(`/classrooms/${encodeURIComponent(id)}/summary`);
}

// --- Roster -----------------------------------------------------------

export interface Student {
  id: string;
  display_name: string;
  suspended: boolean;
  created_at: string;
  /** Phase 4.5c — convenience flag for the UI to surface "promote
   *  to public" availability without needing the actual email. */
  has_parent_contact: boolean;
  /** The actual email, surfaced for the edit field. */
  parent_contact_email: string | null;
}

export interface ResetPinResponse {
  student: Student;
  pin: string;
}

export async function listStudents(classroomId: string): Promise<Student[]> {
  return request<Student[]>(`/classrooms/${encodeURIComponent(classroomId)}/students`);
}

export async function addStudent(
  classroomId: string,
  args: {
    display_name: string;
    pin: string;
    parent_contact_email?: string | null;
  },
): Promise<Student> {
  return request<Student>(`/classrooms/${encodeURIComponent(classroomId)}/students`, {
    method: 'POST',
    body: JSON.stringify(args),
  });
}

export async function updateStudent(
  classroomId: string,
  studentId: string,
  args: { parent_contact_email?: string | null },
): Promise<Student> {
  return request<Student>(
    `/classrooms/${encodeURIComponent(classroomId)}/students/${encodeURIComponent(studentId)}`,
    { method: 'PATCH', body: JSON.stringify(args) },
  );
}

export async function removeStudent(classroomId: string, studentId: string): Promise<void> {
  return request<void>(
    `/classrooms/${encodeURIComponent(classroomId)}/students/${encodeURIComponent(studentId)}`,
    { method: 'DELETE' },
  );
}

export async function resetStudentPin(
  classroomId: string,
  studentId: string,
  pin?: string,
): Promise<ResetPinResponse> {
  const body = pin ? { pin } : {};
  return request<ResetPinResponse>(
    `/classrooms/${encodeURIComponent(classroomId)}/students/${encodeURIComponent(studentId)}/reset-pin`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

// --- Assignments + classroom-assignment join ---------------------------

export interface AssignmentSummary {
  id: string;
  slug: string | null;
  name: string;
  project_kind: 'image' | 'audio' | 'pose';
  time_estimate_minutes: number | null;
  standards_tags: string[];
  is_curated: boolean;
}

export interface RubricLevel {
  name: string;
  description: string;
}

export interface RubricCriterion {
  key: string;
  criterion: string;
  description: string;
  levels: RubricLevel[];
}

export interface AssignmentDetail {
  id: string;
  slug: string | null;
  name: string;
  brief_md: string;
  learning_objectives: string[];
  project_kind: 'image' | 'audio' | 'pose';
  default_class_labels: string[];
  starter_block_program: Record<string, unknown> | null;
  time_estimate_minutes: number | null;
  standards_tags: string[];
  rubric: Array<{
    key: string;
    criterion: string;
    description: string;
    levels: Array<{ name: string; description: string }>;
  }>;
  debrief_questions: string[];
  teacher_notes_md: string | null;
  is_curated: boolean;
}

export interface StandardsBody {
  code: string;
  name: string;
  url: string;
  description: string;
}

export interface StandardsTag {
  code: string;
  body: string;
  label: string;
}

export interface StandardsTaxonomy {
  bodies: StandardsBody[];
  tags_by_body: Record<string, StandardsTag[]>;
}

export interface ListCuratedArgs {
  kind?: 'image' | 'audio' | 'pose';
  standards?: string[];
  q?: string;
}

export async function listCuratedAssignments(
  args: ListCuratedArgs = {},
): Promise<AssignmentSummary[]> {
  const params = new URLSearchParams();
  if (args.kind) params.set('kind', args.kind);
  if (args.standards && args.standards.length > 0) {
    params.set('standards', args.standards.join(','));
  }
  if (args.q) params.set('q', args.q);
  const query = params.toString();
  return request<AssignmentSummary[]>(`/assignments/curated${query ? `?${query}` : ''}`);
}

export async function getStandardsTaxonomy(): Promise<StandardsTaxonomy> {
  return request<StandardsTaxonomy>('/assignments/standards');
}

export async function getAssignment(id: string): Promise<AssignmentDetail> {
  return request<AssignmentDetail>(`/assignments/${encodeURIComponent(id)}`);
}

export interface ClassroomAssignmentEntry {
  id: string;
  classroom_id: string;
  assignment_id: string;
  assignment_name: string;
  assignment_kind: 'image' | 'audio' | 'pose';
  assignment_time_estimate_minutes: number | null;
  starts_at: string | null;
  due_at: string | null;
  status: 'active' | 'archived';
  created_at: string;
}

export async function listClassroomAssignments(
  classroomId: string,
): Promise<ClassroomAssignmentEntry[]> {
  return request<ClassroomAssignmentEntry[]>(
    `/classrooms/${encodeURIComponent(classroomId)}/assignments`,
  );
}

export async function pinAssignmentToClassroom(
  classroomId: string,
  args: { assignment_id: string; starts_at?: string; due_at?: string },
): Promise<ClassroomAssignmentEntry> {
  return request<ClassroomAssignmentEntry>(
    `/classrooms/${encodeURIComponent(classroomId)}/assignments`,
    { method: 'POST', body: JSON.stringify(args) },
  );
}

export async function unpinAssignment(classroomId: string, caId: string): Promise<void> {
  return request<void>(
    `/classrooms/${encodeURIComponent(classroomId)}/assignments/${encodeURIComponent(caId)}`,
    { method: 'DELETE' },
  );
}

// --- Live class view --------------------------------------------------

export interface LiveStudentTile {
  student_id: string;
  display_name: string | null;
  last_seen_at: string | null;
  current_project_id: string | null;
  current_project_name: string | null;
  current_project_kind: 'image' | 'audio' | 'pose' | null;
  sample_count: number;
  submission_status: string | null;
  stuck: boolean;
  /** Most recent blob on the kid's current project — used by the
   *  teacher dashboard for an at-a-glance thumbnail. NULL if the
   *  kid hasn't captured anything yet. */
  latest_sample_blob_id: string | null;
  latest_sample_kind: 'image' | 'audio' | 'pose' | null;
  /** Most recent submission id — lets the teacher broadcast that
   *  submission directly from the live tile. NULL when the kid
   *  hasn't published yet. */
  latest_submission_id: string | null;
}

/** URL for one classroom-scoped sample blob. Teacher-only access on
 *  the backend. Used for live-tile thumbnails. */
export function classroomBlobUrl(classroomId: string, blobId: string): string {
  return `${apiBaseUrl()}/classrooms/${encodeURIComponent(classroomId)}/blobs/${encodeURIComponent(blobId)}`;
}

export async function getLiveView(classroomId: string): Promise<LiveStudentTile[]> {
  return request<LiveStudentTile[]>(`/classrooms/${encodeURIComponent(classroomId)}/live-view`);
}

// --- Review queue + class gallery -------------------------------------

export interface ReviewQueueEntry {
  submission_id: string;
  project_id: string;
  project_name: string;
  project_kind: 'image' | 'audio' | 'pose';
  submitter_id: string;
  submitter_display_name: string | null;
  status: string;
  submitted_at: string;
}

export async function getReviewQueue(classroomId: string): Promise<ReviewQueueEntry[]> {
  return request<ReviewQueueEntry[]>(`/classrooms/${encodeURIComponent(classroomId)}/review-queue`);
}

export async function approveSubmission(
  classroomId: string,
  submissionId: string,
): Promise<ReviewQueueEntry> {
  return request<ReviewQueueEntry>(
    `/classrooms/${encodeURIComponent(classroomId)}/submissions/${encodeURIComponent(submissionId)}/approve`,
    { method: 'POST' },
  );
}

export async function rejectSubmission(
  classroomId: string,
  submissionId: string,
  args: { reason: string; detail?: string },
): Promise<ReviewQueueEntry> {
  return request<ReviewQueueEntry>(
    `/classrooms/${encodeURIComponent(classroomId)}/submissions/${encodeURIComponent(submissionId)}/reject`,
    { method: 'POST', body: JSON.stringify(args) },
  );
}

// --- Gradebook export (Phase 4.5b.15) -----------------------------------

/** URL for the per-classroom gradebook CSV. The teacher's UI links
 *  to this directly with `download` so the browser handles the file
 *  save without round-tripping through fetch + blob. */
export function gradebookCsvUrl(classroomId: string): string {
  return `${apiBaseUrl()}/classrooms/${encodeURIComponent(classroomId)}/gradebook.csv`;
}

// --- Classroom control panel (Phase 4.5b.14) ----------------------------

/** The kid + project the teacher chose to spotlight. Inlined into
 *  ControlState so a kid's overlay renders on the FIRST poll —
 *  no chained fetch. NULL when no broadcast is active. */
export interface BroadcastTarget {
  submission_id: string;
  project_id: string;
  project_name: string;
  project_kind: 'image' | 'audio' | 'pose';
  submitter_display_name: string | null;
  started_at: string;
}

export interface ControlState {
  classroom_id: string;
  paused_at: string | null;
  broadcast: BroadcastTarget | null;
}

export async function getControlState(classroomId: string): Promise<ControlState> {
  return request<ControlState>(`/classrooms/${encodeURIComponent(classroomId)}/control`);
}

export async function pauseClassroom(classroomId: string): Promise<ControlState> {
  return request<ControlState>(`/classrooms/${encodeURIComponent(classroomId)}/control/pause`, {
    method: 'POST',
  });
}

export async function resumeClassroom(classroomId: string): Promise<ControlState> {
  return request<ControlState>(`/classrooms/${encodeURIComponent(classroomId)}/control/resume`, {
    method: 'POST',
  });
}

export async function broadcastSubmission(
  classroomId: string,
  submissionId: string,
): Promise<ControlState> {
  return request<ControlState>(`/classrooms/${encodeURIComponent(classroomId)}/control/broadcast`, {
    method: 'POST',
    body: JSON.stringify({ submission_id: submissionId }),
  });
}

export async function clearBroadcast(classroomId: string): Promise<ControlState> {
  return request<ControlState>(
    `/classrooms/${encodeURIComponent(classroomId)}/control/clear-broadcast`,
    { method: 'POST' },
  );
}

// --- Public-gallery promotion (Phase 4.5c) -------------------------------

export interface RequestPublicResponse {
  submission_id: string;
  parent_contact_email: string;
  sent: boolean;
  /** In non-prod environments, the magic link is surfaced so dev /
   *  e2e can complete without an SMTP server. NULL in production. */
  link: string | null;
}

export async function requestPublicPromotion(
  classroomId: string,
  submissionId: string,
): Promise<RequestPublicResponse> {
  return request<RequestPublicResponse>(
    `/classrooms/${encodeURIComponent(classroomId)}/submissions/${encodeURIComponent(submissionId)}/request-public`,
    { method: 'POST' },
  );
}

export interface PublicConsentSummary {
  submission_id: string;
  child_display_name: string;
  project_name: string;
  project_kind: 'image' | 'audio' | 'pose';
  classroom_name: string;
  decision: 'pending' | 'approved' | 'denied' | 'expired';
  decided_at: string | null;
  expires_at: string;
}

export async function viewPublicConsent(token: string): Promise<PublicConsentSummary> {
  return request<PublicConsentSummary>(`/auth/public-consent/${encodeURIComponent(token)}`);
}

export async function decidePublicConsent(
  token: string,
  decision: 'approve' | 'deny',
): Promise<PublicConsentSummary> {
  return request<PublicConsentSummary>(`/auth/public-consent/${encodeURIComponent(token)}`, {
    method: 'POST',
    body: JSON.stringify({ decision }),
  });
}

export interface ClassGalleryEntry {
  id: string;
  project_id: string;
  project_name: string;
  project_kind: 'image' | 'audio' | 'pose';
  submitter_display_name: string | null;
  submitted_at: string;
  reaction_counts: Record<string, number>;
  my_reactions: string[];
}

export async function getClassGallery(classroomId: string): Promise<ClassGalleryEntry[]> {
  return request<ClassGalleryEntry[]>(`/classrooms/${encodeURIComponent(classroomId)}/gallery`);
}

export interface ReactionsSummary {
  submission_id: string;
  reaction_counts: Record<string, number>;
  my_reactions: string[];
}

export async function addReaction(
  classroomId: string,
  submissionId: string,
  code: string,
): Promise<ReactionsSummary> {
  return request<ReactionsSummary>(
    `/classrooms/${encodeURIComponent(classroomId)}/gallery/${encodeURIComponent(submissionId)}/reactions/${encodeURIComponent(code)}`,
    { method: 'PUT' },
  );
}

export async function removeReaction(
  classroomId: string,
  submissionId: string,
  code: string,
): Promise<ReactionsSummary> {
  return request<ReactionsSummary>(
    `/classrooms/${encodeURIComponent(classroomId)}/gallery/${encodeURIComponent(submissionId)}/reactions/${encodeURIComponent(code)}`,
    { method: 'DELETE' },
  );
}

// --- Feedback + rubric tick-off ---------------------------------------

export interface RubricMark {
  criterion_key: string;
  criterion_label: string;
  criterion_description: string;
  level_name: string;
  level_description: string;
  note_md: string | null;
  updated_at: string;
}

export interface ProjectClassSummary {
  class_label: string;
  sample_count: number;
  /** The kid's most recent sample for this class. Used by the
   *  grading page to render a thumbnail so teachers see what the
   *  kid actually captured. NULL if the class has no samples. */
  sample_blob_id: string | null;
  sample_kind: 'image' | 'audio' | 'pose' | null;
}

/** URL for one sample blob, scoped to the submission so the access
 *  check on the backend can do the cross-project-leakage guard. */
export function submissionBlobUrl(
  classroomId: string,
  submissionId: string,
  blobId: string,
): string {
  return `${apiBaseUrl()}/classrooms/${encodeURIComponent(classroomId)}/submissions/${encodeURIComponent(submissionId)}/blobs/${encodeURIComponent(blobId)}`;
}

export interface FeedbackRead {
  submission_id: string;
  project_id: string;
  project_name: string;
  project_kind: 'image' | 'audio' | 'pose';
  submitter_display_name: string | null;
  submission_status: string;
  /** Phase 4.5c — `class_only` for classroom submissions, `public`
   *  after parent consent. The grading page uses it to decide
   *  whether the "Promote to public" affordance is available. */
  submission_visibility: string;
  /** Phase 4.5c — true when the kid has a parent_contact_email on
   *  the roster. Without this, the Promote action is hidden because
   *  there's nowhere to send the magic link. */
  submitter_has_parent_contact: boolean;
  rubric: RubricCriterion[];
  marks: RubricMark[];
  comment_md: string;
  updated_at: string | null;
  project_classes: ProjectClassSummary[];
}

export interface RubricMarkWrite {
  criterion_key: string;
  level_name: string;
  note_md?: string | null;
}

export interface FeedbackWrite {
  comment_md: string;
  marks: RubricMarkWrite[];
}

export async function getSubmissionFeedback(
  classroomId: string,
  submissionId: string,
): Promise<FeedbackRead> {
  return request<FeedbackRead>(
    `/classrooms/${encodeURIComponent(classroomId)}/submissions/${encodeURIComponent(submissionId)}/feedback`,
  );
}

export async function putSubmissionFeedback(
  classroomId: string,
  submissionId: string,
  body: FeedbackWrite,
): Promise<FeedbackRead> {
  return request<FeedbackRead>(
    `/classrooms/${encodeURIComponent(classroomId)}/submissions/${encodeURIComponent(submissionId)}/feedback`,
    { method: 'PUT', body: JSON.stringify(body) },
  );
}

// --- Published-apps feed (Phase 4.5c) ---------------------------------

export interface ClassroomPublishedApp {
  id: string;
  studentId: string;
  studentName: string;
  projectId: string;
  title: string;
  slug: string;
  version: number;
  url: string;
  viewCount: number;
  updatedAt: string;
}

/** Read-only — every app a student on this classroom roster has
 *  published. Newest first. The teacher sees what their kids
 *  shipped without having to scroll the per-student gallery. */
export async function listClassroomPublishedApps(
  classroomId: string,
): Promise<ClassroomPublishedApp[]> {
  return request<ClassroomPublishedApp[]>(
    `/classrooms/${encodeURIComponent(classroomId)}/published-apps`,
  );
}
